import md5 from 'md5';
import { FC } from 'react';
import { FormProvider } from 'react-hook-form';
import { Row } from 'react-styled-flexboxgrid';
import { useMedia } from 'react-use';

import {
  BUTTON_TYPES,
  media,
  TARGET_LINK,
} from '@savgroup-front-common/constants';
import Bold from '@savgroup-front-common/core/atoms/Bold/Bold';
import { Button } from '@savgroup-front-common/core/atoms/button';
import { Card } from '@savgroup-front-common/core/atoms/Cards';
import { Heading } from '@savgroup-front-common/core/atoms/Heading';
import { Link } from '@savgroup-front-common/core/atoms/link';
import { QualireparLogo } from '@savgroup-front-common/core/atoms/Logo/QualireparLogo/QualireparLogo';
import { PanelBodyLine } from '@savgroup-front-common/core/atoms/Panel/PanelBodyLine/PanelBodyLine';
import { PanelHeading } from '@savgroup-front-common/core/atoms/Panel/PanelHeading/PanelHeading';
import {
  $ActionRow,
  $Description,
  AdaptedQuotationNewModel,
  ConfirmQuotationRejectModal,
  QuotationPendingCustomerValidationContextProvider,
  SparePartQuotationPendingCustomerValidationLine,
  SparePartQuotationPendingCustomerValidationLineSkeleton,
  useQuotationPendingCustomerValidation,
} from '@savgroup-front-common/core/components/Quotation';
import {
  $AmountCell,
  $AmountHeaderCel,
} from '@savgroup-front-common/core/components/Quotation/QuotationLines';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { formatReverseMoneyToString } from '@savgroup-front-common/core/formatters/intl';
import useGetSellerConfigurationById from '@savgroup-front-common/core/hooks/useGetSellerConfigurationById';
import { RecipientType } from '@savgroup-front-common/types';

import { Selectors as CustomerSelectors } from '../../../../../../../domains/Customer';
import { useMyAccountTypedSelector } from '../../../../../../../hooks';
import { useLayout } from '../../../../../../app/NewLayout/NewLayout.hooks';

import { $BonusLine, $CenteredHeading } from './AnswerQuotationContent.styles';
import messages from './messages';
import NonOwnerRecipientSparePartQuotationLine from './NonOwnerRecipientSparePartQuotationLine/NonOwnerRecipientSparePartQuotationLine';
import { QuotationTotal } from './QuotationTotal';

interface AnswerQuotationContentProps {
  quotation: AdaptedQuotationNewModel;
  initialIsOpen?: boolean;
  isOpen?: boolean;
  sellerId: string;
  fileId: string;
  onAnswerSuccess?: () => void;
}

const AnswerQuotationContent: FC<AnswerQuotationContentProps> = ({
  quotation,
  sellerId,
  onAnswerSuccess,
  fileId,
}) => {
  const isMobileView = useMedia(media.maxWidth.xs);
  const fileSummary = useMyAccountTypedSelector(
    CustomerSelectors.selectCustomerFileSummary,
  );
  const { data: sellerConfiguration } = useGetSellerConfigurationById({
    sellerId,
  });

  const { login: owner } = useLayout();

  const {
    formContext,
    adaptedQuotation,
    handleValidate,
    isValidateLoading,
    optionalSpareParts,
    isConfirmRejectModalOpen,
    isRejectLoading,
    handleRejectButtonClick,
    handleRejectWithReturn,
    handleRejectWithDestruction,
    handleCancelConfirmReject,
    isSelectSparePartConditionLoading,
    handleSelectSparePartQuotationLineConditionChange,
    requiredForRepaidSpareParts,
  } = useQuotationPendingCustomerValidation({
    quotation,
    sellerId,
    onAnswerSuccess,
    fileId,
    holderType: fileSummary.fileProducts[0].holder.holderType,
    paymentType: owner.paymentType,
  });
  const { handleSubmit, formState } = formContext;
  const { errors } = formState;

  const shouldDisplayServiceHeader =
    (adaptedQuotation.administrativeFees || [])
      .concat(adaptedQuotation.services || [])
      .concat(adaptedQuotation.unknownServices || [])
      .concat(adaptedQuotation.unknownSpareParts || [])
      .concat(adaptedQuotation.discounts || []).length > 0;

  const { totalAmountToBePaidByClient } = adaptedQuotation;

  if (totalAmountToBePaidByClient.amount === 0) {
    return (
      <>
        <$CenteredHeading>
          <Heading level={5}>
            <SafeFormattedMessageWithoutSpread
              message={messages.quotationPriceIsZeroTitle}
            />
          </Heading>
        </$CenteredHeading>
        <Row center="xs">
          <SafeFormattedMessageWithoutSpread
            message={messages.quotationPriceIsZero}
          />
        </Row>
      </>
    );
  }

  return (
    <>
      <$CenteredHeading>
        <Heading level={5}>
          <SafeFormattedMessageWithoutSpread message={messages.title} />
        </Heading>
      </$CenteredHeading>

      <Card>
        <FormProvider {...formContext}>
          <form onSubmit={handleSubmit(() => handleValidate())}>
            <$Description>{adaptedQuotation.description}</$Description>
            <QuotationPendingCustomerValidationContextProvider
              quotation={adaptedQuotation}
            >
              <div>
                {shouldDisplayServiceHeader && (
                  <>
                    <PanelHeading isActive gridTemplateColumns="10fr 2fr">
                      <div>
                        <SafeFormattedMessageWithoutSpread
                          message={messages.service}
                        />
                      </div>
                      <$AmountHeaderCel>
                        <SafeFormattedMessageWithoutSpread
                          message={messages.total}
                        />
                      </$AmountHeaderCel>
                    </PanelHeading>
                    {(adaptedQuotation.administrativeFees || []).map((fee) => {
                      return (
                        <PanelBodyLine
                          key={fee.preTaxUnitPrice.amount.toString()}
                          isActive
                          gridTemplateColumns="10fr 2fr"
                        >
                          <div>{fee.description}</div>
                          <$AmountCell>
                            {formatReverseMoneyToString(
                              fee.recipient?.recipientType ===
                                RecipientType.OWNER
                                ? fee.amountIncludedTax
                                : {
                                    amount: 0,
                                    currency: fee.amountIncludedTax.currency,
                                  },
                            )}
                          </$AmountCell>
                        </PanelBodyLine>
                      );
                    })}
                    {(adaptedQuotation.services || []).map((service) => {
                      return (
                        <PanelBodyLine
                          key={service.id}
                          isActive
                          gridTemplateColumns="10fr 2fr"
                        >
                          <div>{service.description}</div>
                          <$AmountCell>
                            {formatReverseMoneyToString(
                              service.recipient?.recipientType ===
                                RecipientType.OWNER
                                ? service.amountIncludedTax
                                : {
                                    amount: 0,
                                    currency:
                                      service.amountIncludedTax.currency,
                                  },
                            )}
                          </$AmountCell>
                        </PanelBodyLine>
                      );
                    })}
                    {(adaptedQuotation.unknownServices || []).map((service) => {
                      return (
                        <PanelBodyLine
                          key={md5(
                            formatReverseMoneyToString(
                              service.amountIncludedTax,
                            ),
                          )}
                          isActive
                          gridTemplateColumns="10fr 2fr"
                        >
                          <div>{service.description}</div>
                          <$AmountCell>
                            {formatReverseMoneyToString(
                              service.recipient?.recipientType ===
                                RecipientType.OWNER
                                ? service.amountIncludedTax
                                : {
                                    amount: 0,
                                    currency:
                                      service.amountIncludedTax.currency,
                                  },
                            )}
                          </$AmountCell>
                        </PanelBodyLine>
                      );
                    })}
                    {(adaptedQuotation.unknownSpareParts || []).map(
                      (sparePart) => {
                        return (
                          <PanelBodyLine
                            key={md5(
                              formatReverseMoneyToString(
                                sparePart.amountIncludedTax,
                              ),
                            )}
                            isActive
                            gridTemplateColumns="10fr 2fr"
                          >
                            <div>{sparePart.description}</div>
                            <$AmountCell>
                              {formatReverseMoneyToString(
                                sparePart.recipient?.recipientType ===
                                  RecipientType.OWNER
                                  ? sparePart.amountIncludedTax
                                  : {
                                      amount: 0,
                                      currency:
                                        sparePart.amountIncludedTax.currency,
                                    },
                              )}
                            </$AmountCell>
                          </PanelBodyLine>
                        );
                      },
                    )}
                    {(adaptedQuotation.discounts || []).map((discount) => {
                      return (
                        <PanelBodyLine
                          key={md5(
                            formatReverseMoneyToString(
                              discount.amountIncludedTax,
                            ),
                          )}
                          isActive
                          gridTemplateColumns="10fr 2fr"
                        >
                          <div>{discount.description}</div>
                          <$AmountCell>
                            {formatReverseMoneyToString(
                              discount.recipient?.recipientType ===
                                RecipientType.OWNER
                                ? discount.amountIncludedTax
                                : {
                                    amount: 0,
                                    currency:
                                      discount.amountIncludedTax.currency,
                                  },
                            )}
                          </$AmountCell>
                        </PanelBodyLine>
                      );
                    })}
                  </>
                )}

                {!requiredForRepaidSpareParts && (
                  <SparePartQuotationPendingCustomerValidationLineSkeleton
                    shouldShowRecipientColumn={false}
                  />
                )}

                {!optionalSpareParts && (
                  <SparePartQuotationPendingCustomerValidationLineSkeleton
                    shouldShowRecipientColumn={false}
                  />
                )}

                {requiredForRepaidSpareParts && optionalSpareParts && (
                  <>
                    {requiredForRepaidSpareParts.length > 0 && (
                      <>
                        <PanelHeading
                          isActive
                          gridTemplateColumns="6fr 4fr 2fr"
                        >
                          <div>
                            <SafeFormattedMessageWithoutSpread
                              message={messages.requiredSpareParts}
                            />
                          </div>
                          {isMobileView ? (
                            <div>
                              <SafeFormattedMessageWithoutSpread
                                message={messages.statusSparePartsMobileView}
                              />
                            </div>
                          ) : (
                            <div>
                              <SafeFormattedMessageWithoutSpread
                                message={messages.statusSpareParts}
                              />
                            </div>
                          )}

                          <$AmountHeaderCel>
                            <SafeFormattedMessageWithoutSpread
                              message={messages.total}
                            />
                          </$AmountHeaderCel>
                        </PanelHeading>

                        {requiredForRepaidSpareParts
                          .filter(
                            (sparePart) =>
                              sparePart.recipient?.data === RecipientType.OWNER,
                          )
                          .map((sparePartQuotationLine, index) => {
                            return (
                              <SparePartQuotationPendingCustomerValidationLine
                                key={index}
                                shouldShowRecipientColumn={false}
                                onSelectSparePartQuotationLineChange={
                                  handleSelectSparePartQuotationLineConditionChange
                                }
                                sparePartQuotationLine={sparePartQuotationLine}
                                index={index}
                              />
                            );
                          })}

                        {requiredForRepaidSpareParts
                          .filter(
                            (sparePart) =>
                              sparePart.recipient?.data !== RecipientType.OWNER,
                          )
                          .map((sparePartQuotationLine) => {
                            return (
                              <NonOwnerRecipientSparePartQuotationLine
                                key={sparePartQuotationLine.id}
                                sparePartQuotationLine={sparePartQuotationLine}
                              />
                            );
                          })}
                      </>
                    )}

                    {optionalSpareParts.length > 0 && (
                      <>
                        <PanelHeading
                          isActive
                          gridTemplateColumns="6fr 4fr 2fr"
                        >
                          <div>
                            <SafeFormattedMessageWithoutSpread
                              message={messages.optionalSpareParts}
                            />
                          </div>
                          {isMobileView ? (
                            <div>
                              <SafeFormattedMessageWithoutSpread
                                message={messages.statusSparePartsMobileView}
                              />
                            </div>
                          ) : (
                            <div>
                              <SafeFormattedMessageWithoutSpread
                                message={messages.statusSpareParts}
                              />
                            </div>
                          )}
                          <$AmountHeaderCel>
                            <SafeFormattedMessageWithoutSpread
                              message={messages.total}
                            />
                          </$AmountHeaderCel>
                        </PanelHeading>

                        {optionalSpareParts
                          .filter(
                            (sparePart) =>
                              sparePart.recipient?.data === RecipientType.OWNER,
                          )
                          .map((sparePartQuotationLine, index) => {
                            return (
                              <SparePartQuotationPendingCustomerValidationLine
                                key={index}
                                shouldShowRecipientColumn={false}
                                onSelectSparePartQuotationLineChange={
                                  handleSelectSparePartQuotationLineConditionChange
                                }
                                sparePartQuotationLine={sparePartQuotationLine}
                                index={index}
                                preDataTestId="optionalSpareParts"
                              />
                            );
                          })}

                        {optionalSpareParts
                          .filter(
                            (sparePart) =>
                              sparePart.recipient?.data !==
                                RecipientType.OWNER &&
                              sparePart.isSelectedForRepair,
                          )
                          .map((sparePartQuotationLine, index) => {
                            return (
                              <NonOwnerRecipientSparePartQuotationLine
                                key={index}
                                sparePartQuotationLine={sparePartQuotationLine}
                              />
                            );
                          })}
                      </>
                    )}
                  </>
                )}
              </div>

              <QuotationTotal fileId={fileId} quotation={adaptedQuotation} />

              {quotation.bonus && !quotation.isBonusRemoved && (
                <$BonusLine data-testid="bonus">
                  <QualireparLogo />
                  <div>
                    <Bold>
                      <SafeFormattedMessageWithoutSpread
                        message={messages.congratulation}
                      />
                    </Bold>
                    <p>
                      <SafeFormattedMessageWithoutSpread
                        message={messages.bonusDidactic}
                      />
                      {sellerConfiguration?.qualiReparUrl && (
                        <Link
                          isExternal
                          href={sellerConfiguration?.qualiReparUrl}
                          target={TARGET_LINK.TARGET_BLANK}
                        >
                          <SafeFormattedMessageWithoutSpread
                            message={messages.learnMore}
                          />
                        </Link>
                      )}
                    </p>
                  </div>
                </$BonusLine>
              )}
            </QuotationPendingCustomerValidationContextProvider>

            <$ActionRow>
              <Button
                type={BUTTON_TYPES.BUTTON}
                danger
                small
                onClick={() => handleRejectButtonClick()}
                isLoading={isSelectSparePartConditionLoading || isRejectLoading}
              >
                <SafeFormattedMessageWithoutSpread
                  message={messages.buttonReject}
                />
              </Button>
              <Button
                type={BUTTON_TYPES.SUBMIT}
                success
                small
                isLoading={
                  isSelectSparePartConditionLoading || isValidateLoading
                }
                isError={Object.keys(errors).length > 0}
                disabled={!!Object.keys(formState.errors).length}
              >
                <SafeFormattedMessageWithoutSpread
                  message={messages.buttonValidate}
                />
              </Button>
            </$ActionRow>
          </form>
        </FormProvider>
        <ConfirmQuotationRejectModal
          isConfirmRejectModalOpen={isConfirmRejectModalOpen}
          handleCancelConfirmReject={handleCancelConfirmReject}
          handleRejectWithDestruction={handleRejectWithDestruction}
          handleRejectWithReturn={handleRejectWithReturn}
          quotation={quotation}
        />
      </Card>
    </>
  );
};

AnswerQuotationContent.displayName = 'AnswerQuotationContent';

export default AnswerQuotationContent;
